// src/pages/RideBookingsPublicView.jsx

import React, { useEffect, useState } from "react";
import {
  Search,
  Filter,
  RefreshCw,
  Eye,
  Phone,
  MapPin,
  CalendarDays,
  Users,
  Inbox,
  CheckCircle2,
  Clock,
  XCircle,
  TrendingUp,
  Bell,
} from "lucide-react";
import { rideService } from "../services/rideService";

const STATUS_META = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", dot: "bg-yellow-500" },
  contacted: { label: "Contacted", color: "bg-blue-100 text-blue-800", dot: "bg-blue-500" },
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-800", dot: "bg-green-500" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", dot: "bg-red-500" },
  completed: { label: "Completed", color: "bg-gray-200 text-gray-800", dot: "bg-gray-500" },
};

const StatCard = ({ icon: Icon, label, value, tone = "default" }) => {
  const tones = {
    default: "bg-white border-[#E2E8E5] text-[#061A3A]",
    brand: "bg-[#F3FBF7] border-[#00A260]/20 text-[#005543]",
    warn: "bg-yellow-50 border-yellow-200 text-yellow-800",
    danger: "bg-red-50 border-red-200 text-red-700",
    success: "bg-green-50 border-green-200 text-green-800",
  };

  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">
          {label}
        </p>
      </div>
      <p className="mt-2 text-2xl font-extrabold">{value}</p>
    </div>
  );
};

/* ------------------------------------------------------------
   Simple inline notifications panel (no auth)
------------------------------------------------------------ */
const NotificationsPanel = ({ items, onSelect }) => {
  if (!items.length) return null;

  return (
    <div className="mb-6 rounded-2xl border border-[#E2E8E5] bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#00A260]/10 text-[#00A260]">
          <Bell className="h-4 w-4" />
        </div>
        <p className="text-sm font-bold text-[#061A3A]">Recent Notifications</p>
      </div>

      <div className="space-y-2">
        {items.slice(0, 5).map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => onSelect?.(n)}
            className={`flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
              n.is_read
                ? "border-[#E2E8E5] bg-white hover:bg-[#F8FAF9]"
                : "border-[#00A260]/30 bg-[#F3FBF7] hover:bg-[#E8F8F1]"
            }`}
          >
            <div
              className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                n.is_read ? "bg-[#E2E8E5]" : "bg-[#00A260]"
              }`}
            />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-[#061A3A]">{n.title}</p>
              <p className="mt-0.5 line-clamp-2 text-[11px] text-[#64748B]">
                {n.message}
              </p>
              <p className="mt-1 text-[10px] text-[#94A3B8]">
                {new Date(n.created_at).toLocaleString("en-IN")}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------
   Detail modal (lightweight — no drawer, works on any screen)
------------------------------------------------------------ */
const DetailModal = ({ booking, onClose }) => {
  if (!booking) return null;

  const meta = STATUS_META[booking.status] || STATUS_META.pending;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 backdrop-blur-sm p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[#E2E8E5] px-5 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#00A260]">
              Ride Booking
            </p>
            <h3 className="mt-1 text-lg font-extrabold text-[#061A3A]">
              {booking.customer_name}
            </h3>
            <p className="mt-0.5 text-xs text-[#94A3B8]">
              {new Date(booking.created_at).toLocaleString("en-IN")}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[#E2E8E5] px-3 py-1.5 text-xs font-semibold text-[#64748B] hover:bg-[#F1F5F3]"
          >
            Close
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <span
            className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase ${meta.color}`}
          >
            {meta.label}
          </span>

          <div className="mt-4 space-y-4">
            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                Customer
              </p>
              <div className="space-y-1.5 text-sm text-[#061A3A]">
                <p><span className="text-[#94A3B8]">Name:</span> {booking.customer_name}</p>
                <p><span className="text-[#94A3B8]">Phone:</span> {booking.phone}</p>
                <p><span className="text-[#94A3B8]">WhatsApp:</span> {booking.whatsapp || "—"}</p>
                <p><span className="text-[#94A3B8]">Email:</span> {booking.email || "—"}</p>
              </div>
            </div>

            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                Journey
              </p>
              <div className="space-y-1.5 text-sm text-[#061A3A]">
                <p><span className="text-[#94A3B8]">Pickup:</span> {booking.pickup_location}</p>
                <p><span className="text-[#94A3B8]">Drop:</span> {booking.drop_location || "—"}</p>
                <p><span className="text-[#94A3B8]">Travel Date:</span> {booking.travel_date || "—"}</p>
                <p><span className="text-[#94A3B8]">Pickup Time:</span> {booking.pickup_time || "—"}</p>
                <p><span className="text-[#94A3B8]">Return Date:</span> {booking.return_date || "—"}</p>
                <p><span className="text-[#94A3B8]">Passengers:</span> {booking.passengers || "—"}</p>
              </div>
            </div>

            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                Vehicle & Service
              </p>
              <div className="space-y-1.5 text-sm text-[#061A3A]">
                <p><span className="text-[#94A3B8]">Vehicle:</span> {booking.vehicle_type || "—"}</p>
                <p><span className="text-[#94A3B8]">Service:</span> {booking.service_type || "—"}</p>
              </div>
            </div>

            {booking.requirements ? (
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                  Requirements
                </p>
                <p className="text-sm text-[#061A3A]">{booking.requirements}</p>
              </div>
            ) : null}

            {booking.admin_notes ? (
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                  Admin Notes
                </p>
                <p className="text-sm text-[#061A3A]">{booking.admin_notes}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------
   Main page
------------------------------------------------------------ */
const RideBookingsPublicView = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const [list, s, notifs] = await Promise.all([
        rideService.listRideBookings({
          status: statusFilter || undefined,
          search: search || undefined,
        }),
        rideService.getRideBookingStats(),
        rideService
          .listAdminNotifications({ limit: 10 })
          .catch(() => []),
      ]);
      setItems(list);
      setStats(s);
      setNotifications(notifs || []);
    } catch (err) {
      console.error("Load ride bookings error:", err);
      setError(err?.message || "Unable to load ride bookings");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const handleNotificationSelect = (n) => {
    if (n.ref_table !== "ride_bookings" || !n.ref_id) return;
    const found = items.find((x) => x.id === n.ref_id);
    if (found) setSelected(found);
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* HEADER */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#00A260]/10 text-[#00A260]">
                <Inbox className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-extrabold text-[#061A3A] sm:text-2xl">
                  Ride Bookings
                </h1>
                <p className="text-xs text-[#64748B]">
                  Live feed of all customer ride requests
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#E2E8E5] bg-white px-4 text-xs font-semibold text-[#061A3A] transition hover:border-[#00A260] hover:text-[#00A260]"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        {/* NOTIFICATIONS */}
        <NotificationsPanel
          items={notifications}
          onSelect={handleNotificationSelect}
        />

        {/* ERROR */}
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* STATS */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <StatCard
            icon={TrendingUp}
            label="Total"
            value={stats?.total ?? "—"}
            tone="brand"
          />
          <StatCard
            icon={Clock}
            label="Pending"
            value={stats?.byStatus?.pending ?? "—"}
            tone="warn"
          />
          <StatCard
            icon={Phone}
            label="Contacted"
            value={stats?.byStatus?.contacted ?? "—"}
          />
          <StatCard
            icon={CheckCircle2}
            label="Confirmed"
            value={stats?.byStatus?.confirmed ?? "—"}
            tone="success"
          />
          <StatCard
            icon={XCircle}
            label="Cancelled"
            value={stats?.byStatus?.cancelled ?? "—"}
            tone="danger"
          />
        </div>

        {/* FILTER BAR */}
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-[#E2E8E5] bg-white p-3 sm:flex-row sm:items-center">
          <form
            onSubmit={handleSearchSubmit}
            className="flex flex-1 items-center gap-2"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, phone, pickup, drop…"
                className="h-11 w-full rounded-xl border border-[#E2E8E5] bg-[#FAFCFB] pl-10 pr-4 text-sm text-[#061A3A] outline-none focus:border-[#00A260] focus:ring-4 focus:ring-[#00A260]/10"
              />
            </div>

            <button
              type="submit"
              className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-[#00A260] px-4 text-xs font-bold text-white hover:bg-[#005543]"
            >
              Search
            </button>
          </form>

          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-[#94A3B8]" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-11 rounded-xl border border-[#E2E8E5] bg-white px-3 text-xs font-semibold text-[#061A3A] outline-none focus:border-[#00A260] focus:ring-4 focus:ring-[#00A260]/10"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* LIST */}
        {loading ? (
          <div className="rounded-2xl border border-[#E2E8E5] bg-white p-12 text-center">
            <span className="mx-auto block h-8 w-8 animate-spin rounded-full border-4 border-[#00A260]/20 border-t-[#00A260]" />
            <p className="mt-3 text-sm text-[#64748B]">
              Loading ride bookings…
            </p>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E2E8E5] bg-white p-12 text-center">
            <Inbox className="mx-auto h-8 w-8 text-[#CBD5D1]" />
            <p className="mt-3 text-sm font-semibold text-[#64748B]">
              No ride bookings found
            </p>
            <p className="mt-1 text-xs text-[#94A3B8]">
              Submit a booking from the public form and refresh.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-2xl border border-[#E2E8E5] bg-white lg:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#FAFCFB] text-[10px] uppercase tracking-wider text-[#64748B]">
                  <tr>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Journey</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const meta =
                      STATUS_META[r.status] || STATUS_META.pending;
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-[#F1F5F3] hover:bg-[#FAFCFB]"
                      >
                        <td className="px-4 py-3">
                          <p className="font-semibold text-[#061A3A]">
                            {r.customer_name}
                          </p>
                          <p className="text-[11px] text-[#94A3B8]">
                            {r.passengers
                              ? `${r.passengers} passenger(s)`
                              : ""}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">
                          <p>{r.phone}</p>
                          {r.email ? (
                            <p className="truncate text-[10px] text-[#94A3B8]">
                              {r.email}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">
                          <p className="truncate max-w-[180px]">
                            {r.pickup_location} → {r.drop_location || "—"}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">
                          <p>{r.travel_date || "—"}</p>
                          {r.pickup_time ? (
                            <p className="text-[10px] text-[#94A3B8]">
                              {r.pickup_time}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${meta.color}`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${meta.dot}`}
                            />
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[11px] text-[#94A3B8]">
                          {new Date(r.created_at).toLocaleString("en-IN")}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setSelected(r)}
                            className="inline-flex items-center gap-1 rounded-lg border border-[#E2E8E5] bg-white px-2.5 py-1.5 text-[11px] font-semibold text-[#061A3A] hover:border-[#00A260] hover:text-[#00A260]"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-3 lg:hidden">
              {items.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelected(r)}
                    className="w-full rounded-2xl border border-[#E2E8E5] bg-white p-4 text-left transition hover:border-[#00A260]/40"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#061A3A]">
                          {r.customer_name}
                        </p>
                        <p className="text-xs text-[#64748B]">{r.phone}</p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${meta.color}`}
                      >
                        {meta.label}
                      </span>
                    </div>

                    <div className="mt-3 space-y-1.5 text-xs text-[#64748B]">
                      <p className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-[#00A260]" />
                        <span className="truncate">
                          {r.pickup_location} → {r.drop_location || "—"}
                        </span>
                      </p>
                      <p className="flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 text-[#00A260]" />
                        {r.travel_date || "—"}{" "}
                        {r.pickup_time ? `@ ${r.pickup_time}` : ""}
                      </p>
                      {r.passengers ? (
                        <p className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-[#00A260]" />
                          {r.passengers} passenger(s)
                        </p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* DETAIL MODAL */}
      <DetailModal booking={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default RideBookingsPublicView;